<template>
  <div>
    <v-dialog v-model="show" max-width="1200" scrollable>
      <v-card>
        <v-card-title>
          <v-col cols="6" class="v-card__title pa-0">
            {{ (this.invoice ? 'Edit' : 'Create') + ' Project Invoice' }}
          </v-col>
          <div class="v-card__title pa-0 ml-3">
            Reported Total: {{ calculatedInvoiceTotal ?? 0 }}
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="client-invoice py-3">
          <v-row>
            <v-col cols="5">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select dense :items="employee" label="Creator" item-value="full_name" item-text="full_name"
                    v-model="creator" hide-details>
                    <template v-slot:prepend-item>
                      <div class="px-4 py-2">
                        <v-text-field dense hide-details label="Search creator" v-model="searchEmployee"></v-text-field>
                      </div>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select dense hide-details :items="divisions" label="Division" item-value="Name" item-text="Name"
                    v-model="division"></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select :items="projects" item-value="Project_ID" item-text="Description" label="Project"
                    :disabled="!!(invoice && invoice.Invoice_ID)" v-model="project" dense hide-details>
                    <template v-slot:prepend-item>
                      <div class="px-4 py-2">
                        <v-text-field dense hide-details label="Search project" v-model="searchProject"></v-text-field>
                      </div>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6" class="px-2">
                  <RangeDatePickerMenu dense hide-details v-model="invoiceInterval" label="Invoice Interval" showButtons
                    @onOkClick="updateInvoiceInterval" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="invoiceNumber" label="Invoice number" hide-details dense></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <DatePicker dense hide-details v-model="invoiceD" label="Invoice Date" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-combobox v-model="paymentP" :items="paymentPeriods" item-value="val" item-text="title"
                    label="Payment Period (days)" hide-details dense type="number" clearable></v-combobox>
                </v-col>
                <v-col cols="12" md="6">
                  <DatePicker dense hide-details v-model="invoiceDD" label="Due Date" :min="invoiceDate" />
                </v-col>
                <v-col cols="12">
                  <v-textarea dense hide-details label="Notes" rows="1" v-model="notes"
                    class="invoice-notes"></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-data-table dense class="data-table" fixed-header :headers="headers" :items="invoicePayments"
                    :items-per-page="5" :footer-props="{
                  'items-per-page-options': [5, 10, 20, 50, -1]
                }" @click:row="onRowClick">
                    <template v-slot:[`item.Invoice_Date`]="{ item }">
                      {{ item.Invoice_Date | dateFilter }}
                    </template>
                    <template v-slot:[`item.Payment_Date`]="{ item }">
                      {{ item.Payment_Date | dateFilter }}
                    </template>
                    <template v-slot:[`item.Payment_Total`]="{ item }">
                      {{ item.Payment_Total ? (+item.Payment_Total).toFixed(2) : item.Payment_Total }}
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="7">
              <div class="table-container">
                <v-data-table style="border-radius: 0" dense class="elevation-0" :headers="headersEmployee"
                  :items="employeeInvoices" item-key="ID" :calculate-widths="false" :mobile-breakpoint="400">
                  <!-- Name Column -->
                  <template v-slot:[`item.Full_Name`]="{ item: nestedItem }">
                    <span>{{ nestedItem.Full_Name }}</span>
                  </template>

                  <!-- Hours Column -->
                  <template v-slot:[`item.Invoice_Dur`]="{ item: nestedItem }">
                    <span>{{ nestedItem.Invoice_Dur }}</span>
                  </template>

                  <!-- Editable Hours Column (+/-) -->
                  <template v-slot:[`item.Modified_Invoice_Dur`]="{ item: nestedItem }">
                    <v-text-field
                      v-model.number="nestedItem.Modified_Hour" dense hide-details type="number" class="custom-input">
                    </v-text-field>
                  </template>

                  <!-- Rate Column -->
                  <template v-slot:[`item.Rate`]="{ item: nestedItem }">
                    <span>{{ nestedItem.Rate }}</span>
                  </template>

                  <!-- Editable Rate Column (+/-) -->
                  <template v-slot:[`item.Modified_Rate_Dur`]="{ item: nestedItem }">
                    <v-text-field
                      v-model.number="nestedItem.Modified_Rate" dense hide-details type="number" class="custom-input">
                    </v-text-field>
                  </template>

                  <!-- Notes (Dynamic Calculation) -->
                  <template v-slot:[`item.Invoice_Sum`]="{ item: nestedItem }">
                    <span>{{ calculateTotal(nestedItem) }}</span>
                  </template>

                  <template v-slot:[`item.Notes`]="{ item: nestedItem }">
                    <v-text-field
                      v-model.number="nestedItem.Notes" dense hide-details type="text" class="custom-input">
                    </v-text-field>
                  </template>
                </v-data-table>
              </div>
              <v-col class="total-container">
                <v-text-field
                :value="invoiceDiscount" @input="updateDiscount" label="Discount" hide-details dense type="number">
                </v-text-field>
                <v-text-field
                  :value="totalPayment" label="Invoice total" readonly hide-details dense @blur="onTotalPaymentBlur" style="pointer-events: none; font-weight: bold;" >
                </v-text-field>
              </v-col>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="invoice && invoice.Invoice_ID" small color="red darken-2 white--text"
            @click="deleteInvoice(false)">delete invoice</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="invoice && invoice.Invoice_ID" small color="success" :disabled="invoice && !invoice.Invoice_ID"
            @click="createInvoicePayment">Add Payment</v-btn>
          <v-btn small :color="invoice && invoice.Invoice_ID ? 'primary' : 'success'"
            :disabled="!isInvoiceSet || saveInProgress" @click="serveInvoice">{{ invoice && invoice.Invoice_ID ?
            'Update' : 'Create' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="invoicePaymentDialog" max-width="500">
      <v-card>
        <v-card-title>
          {{ (this.currentInvoicePayment ? 'Edit' : 'New') + ' Project Payment' }}
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="cancelEditingInvoicePayment">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <b>{{ 'Invoice Number: ' + this.invoiceNumber }}</b>
            </v-col>
            <v-col>
              <b>{{ 'Project Name: ' + this.project }}</b>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <b>{{ 'Invoice Date: ' + this.invoiceD}}</b>
            </v-col>
            <v-col>
              <b>{{ 'Client Name: ' + this.currentInvoicePayment.Client_Name }}</b>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <DatePicker v-model="currentInvoicePayment.Payment_Date" label="Date" :min="invoiceDate" />
            </v-col>
            <v-col>
              <v-text-field label="Total" v-model="currentInvoicePayment.Payment_Total"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="currentInvoicePayment.Payment_ID" small color="red darken-2 white--text"
            @click="deleteInvoicePayment">Delete</v-btn>
          <v-btn small color="primary" @click="addInvoicePayment" :disabled="addInvoicePaymentSaveDisable">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog v-if="showConfirmDeleteDialog" v-model="showConfirmDeleteDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="invoice && invoice.Invoice_Number ? `Delete invoice #${invoice.Invoice_Number}.` : 'Are you sure?'"
      @onDeleteClick="deleteInvoice(true)" @onCancelClick="showConfirmDeleteDialog = false"
      @onCloseClick="showConfirmDeleteDialog = false" />
    <ConfirmOrDeniedDialog v-if="showDeniedDialog" v-model="showDeniedDialog" :mode="confirmOrDeniedDialogModes.denied"
      :message="'The current project does not have a rate. Please add a project rate before creating an invoice.'"
      @onOkClick="showDeniedDialog = false" @onCloseClick="showDeniedDialog = false" />
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'InvoiceEditor',
  components: {
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    invoice: {
      required: false,
      default: null
    },
    invoicePayments: {
      required: false,
      default: []
    }
  },
  data () {
    return {
      addInvoicePaymentSaveDisable: false,
      creator: null,
      division: '',
      project: null,
      department: null,
      invoiceNumber: null,
      invoiceTotal: null,
      invoiceDiscount: null,
      paymentPeriod: 0,
      invoiceInterval: [],
      invoiceDate: formatLocalDate(),
      invoiceDueDate: null,
      invoiceIntervalDatesMenu: false,
      notes: '',
      searchEmployee: '',
      searchClient: '',
      searchProject: '',
      showDeniedDialog: false,
      invoicePaymentDialog: false,
      currentInvoicePayment: {
        Payment_ID: null,
        Client_Name: '',
        Payment_Date: formatLocalDate(),
        Payment_Total: 0
      },
      invoicePaymentDateMenu: false,
      headers: [
        {
          text: 'Project',
          value: 'Project_Name',
          divider: true
        },
        {
          text: 'Client Name',
          value: 'Client_Name',
          divider: true
        },
        {
          text: 'Invoice Date',
          value: 'Invoice_Date',
          divider: true
        },
        {
          text: 'Payment Date',
          value: 'Payment_Date',
          divider: true
        },
        {
          text: 'Payment Total',
          value: 'Payment_Total',
          divider: true
        }
      ],
      headersEmployee: [
        {
          text: 'Employee',
          value: 'Full_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Hours',
          value: 'Invoice_Dur',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '+/-',
          value: 'Modified_Invoice_Dur',
          width: 70,
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Rate',
          value: 'Rate',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '+/-',
          width: 70,
          value: 'Modified_Rate_Dur',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Total',
          value: 'Invoice_Sum',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Notes',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        }
      ],
      paymentPeriods: [
        {
          title: 'Due on receipt',
          val: 0
        },
        {
          title: '10 days',
          val: 10
        },
        {
          title: '30 days',
          val: 30
        },
        {
          title: '60 days',
          val: 60
        }
      ],
      employeeInvoices: [],
      saveInProgress: false,
      showConfirmDeleteDialog: false,
      copyInvoice: null,
      currentEmployeeData: null,
      rules: {
        required: v => !!v || 'Required',
        noZero: value => value !== '0' || 'Zero is not allowed'
      }
    }
  },
  async mounted () {
    if (!this.employee.length) {
      await this.getEmployee()
    } else {
      if (this.invoice) {
        this.creator = this.employee.find(item => item.employee_id === this.invoice.Invoice_Creator).full_name
      } else {
        this.creator = this.employee.find(item => item.employee_id === this.user.employee_id).full_name
      }
    }
    if (!this.divisions.length) {
      await this.getDivisions()
    }
    if (this.invoice) {
      this.division = this.invoice ? this.invoice.Division_Name : null
      this.project = this.invoice ? this.invoice.Project_ID : null
      this.invoiceInterval = this.invoice ? [this.invoice.Period_From, this.invoice.Period_To] : []
      this.invoiceNumber = this.invoice ? this.invoice.Invoice_Number : null
      this.invoiceDate = this.invoice ? this.invoice.Invoice_Date : null
      this.invoiceTotal = this.invoice ? this.invoice.Invoice_Total : null
      this.invoiceDD = this.invoice ? this.invoice.Due_Date : null
      this.notes = this.invoice ? this.invoice.Notes : ''
      this.invoiceDiscount = this.invoice.Discount !== undefined && this.invoice.Discount !== null && this.invoice.Discount !== 0
        ? this.invoice.Discount
        : null
      if (this.invoice.Invoice_ID && this.project && this.invoiceInterval.length === 2) {
        this.getInvoiceTotal()
      }
    }
    if (this.invoice && this.invoice.Invoice_ID) {
      this.copyInvoice = Object.assign(this.invoice)
    }
  },
  computed: {
    calculatedInvoiceTotal () {
      return this.employeeInvoices.reduce((total, item) => {
        return total + this.calculateTotal(item)
      }, 0)
    },
    totalPayment () {
      const calculatedTotal = this.calculatedInvoiceTotal || 0
      const discount = parseFloat(this.invoiceDiscount) || 0

      if (this.invoice?.Invoice_ID) {
        return this.invoiceTotal !== null ? this.invoiceTotal : calculatedTotal - discount
      }

      return calculatedTotal - discount
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    projects () {
      const r = new RegExp(this.searchProject, 'gi')
      return this.$store.getters.getProjects.filter(item => {
        r.lastIndex = 0
        return r.test(item.Description)
      })
    },
    clients () {
      const r = new RegExp(this.searchClient, 'gi')
      return this.$store.getters.getClients.filter(item => {
        r.lastIndex = 0
        return r.test(item.Name)
      })
    },
    divisions () {
      return this.$store.getters.getDivisions
    },
    user () {
      return this.$store.getters.getUser
    },
    employee () {
      const r = new RegExp(this.searchEmployee, 'gi')
      return this.$store.getters.getEmployee.filter(item => {
        r.lastIndex = 0
        return r.test(item.full_name)
      })
    },
    invoiceD: {
      get () {
        return this.invoiceDate
      },
      set (val) {
        this.invoiceDate = val
        this.paymentPeriod = this.differenceBetween2Dates(val, this.invoiceDueDate)
        if (this.invoiceDueDate && (new Date(val) > new Date(this.invoiceDD))) {
          this.invoiceDueDate = val
        }
      }
    },
    invoiceDD: {
      get () {
        return this.invoiceDueDate
      },
      set (val) {
        this.invoiceDueDate = val
        this.paymentPeriod = this.differenceBetween2Dates(this.invoiceDate, val)
      }
    },
    paymentP: {
      get () {
        return this.paymentPeriod
      },
      set (val) {
        this.paymentPeriod = typeof val === 'object' ? val?.val : val
        this.$nextTick(() => {
          if (this.invoice) {
            this.invoiceTotal = this.calculatedInvoiceTotal - +this.invoiceDiscount
          }
          this.invoiceDueDate = this.addDaysToDate(this.invoiceDate, this.paymentPeriod)
        })
      }
    },
    isInvoiceSet () {
      if (
        this.copyInvoice &&
        this.invoiceInterval &&
        this.creator &&
        this.project &&
        this.invoiceNumber &&
        this.invoiceDate &&
        this.invoiceDueDate &&
        this.division &&
        this.notes.length >= 0
      ) {
        let findedEmplId = this.employee.find(e => e.full_name === this.creator)
        if (findedEmplId) {
          findedEmplId = findedEmplId.employee_id
        } else {
          return false
        }
        return (
          this.copyInvoice.Period_From !== this.invoiceInterval[0] ||
          this.copyInvoice.Period_To !== this.invoiceInterval[1] ||
          +this.copyInvoice.Invoice_Creator !== +findedEmplId ||
          this.copyInvoice.Project_Name !== this.project ||
          this.copyInvoice.Invoice_Number !== '' + this.invoiceNumber ||
          this.copyInvoice.Invoice_Date !== this.invoiceDate ||
          this.copyInvoice.Due_Date !== this.invoiceDueDate ||
          this.copyInvoice.Notes !== this.notes ||
          this.copyInvoice.Division_Name !== this.division
        )
      }
      return !!(
        (this.invoiceInterval.length === 2) &&
        this.creator &&
        this.project &&
        this.invoiceNumber &&
        this.invoiceDate &&
        this.invoiceDueDate
      )
    }
  },
  methods: {
    calculateTotal (item) {
      const updatedHours = (parseFloat(item.Modified_Hour) || 0) + (parseFloat(item.Invoice_Dur))
      const updatedRate = (parseFloat(item.Modified_Rate) || 0) + (parseFloat(item.Rate))
      return Math.round(updatedHours * updatedRate * 100) / 100
    },
    recalculateTotalWithDiscount () {
      const calculatedTotal = this.calculatedInvoiceTotal || 0
      const discount = parseFloat(this.invoiceDiscount) || 0

      if (this.invoice?.Invoice_ID) {
        this.invoiceTotal = calculatedTotal - discount
      } else {
        this.invoiceTotal = calculatedTotal - discount
      }
    },
    updateDiscount (value) {
      this.invoiceDiscount = value
      // in case of editing old invoice
      // if (this.invoice?.Invoice_ID) {
      //   const total = value ? this.invoiceTotal + +this.invoice.Discount - +value : this.invoiceTotal + +this.invoice.Discount
      //   this.invoiceTotal = total
      // } else {
      // }

      this.invoiceTotal = this.calculatedInvoiceTotal - +value
    },
    getInvoiceTotal () {
      this.axios.post('/api/invoice/total', {
        selectedProject: this.project,
        dateFrom: this.invoiceInterval[0],
        dateTo: this.invoiceInterval[1],
        invoiceID: this.invoice ? this.invoice.Invoice_ID : null
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.employeeInvoices = res.data.map(invoice => ({ ...invoice }))
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteInvoice (confirmed) {
      if (confirmed) {
        this.axios.delete(`/api/invoice/${this.invoice.Invoice_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(response => {
          this.$store.dispatch('setSuccess', { successMsg: `Invoice # ${this.invoice.Invoice_Number} successfully removed` })
          this.showConfirmDeleteDialog = false
          this.show = false
          this.$emit('invoiceDeleted')
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.showConfirmDeleteDialog = true
      }
    },
    onTotalPaymentBlur () {
      this.totalPayment = this.totalPayment ? (+this.totalPayment).toFixed(2) : this.totalPayment
    },
    async getEmployee () {
      await this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
        if (this.invoice) {
          this.creator = response.data.find(item => item.employee_id === this.invoice.Invoice_Creator).full_name
        } else {
          this.creator = response.data.find(item => item.employee_id === this.user.employee_id).full_name
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getDivisions () {
      await this.axios.get('/api/divisionList', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setDivisionList', { divisions: response.data })
        this.division = response.data[0].Name
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateInvoiceInterval (e) {
      const d1 = +(new Date(e[0]))
      const d2 = +(new Date(e[1]) || new Date(e[0]))
      if (d1 < d2) this.invoiceInterval = [e[0], e[1]]
      else if (d1 > d2) this.invoiceInterval = [e[1], e[0]]
      else this.invoiceInterval = [e[0], e[1]]
      if (d1 && d2) this.invoiceIntervalDatesMenu = false
    },
    clearAll () {
      this.creator = null
      this.project = null
      this.department = null
      this.invoiceInterval = []
      this.invoiceDate = formatLocalDate()
      this.invoiceDueDate = null
      this.invoiceTotal = null
      this.invoiceDiscount = null
      this.invoiceNumber = null
      this.division = this.divisions[0]
      this.paymentPeriod = 0
      this.notes = ''
      this.searchEmployee = ''
      this.searchClient = ''
      this.searchProject = ''
      this.searchDepartment = ''
    },
    serveInvoice () {
      this.saveInProgress = true
      const c = this.employee.find(item => item.full_name === this.creator)
      if (!c) {
        this.$store.dispatch('setError', { errorMsg: 'Creator is invalid' })
        this.saveInProgress = false
        return
      }
      const creatorId = c.employee_id
      const p = this.projects.find(item => item.Project_ID === this.project)
      if (!p) {
        this.$store.dispatch('setError', { errorMsg: 'Project is invalid' })
        this.saveInProgress = false
        return
      } else if (p && !p.HasRate) {
        this.saveInProgress = false
        this.showDeniedDialog = true
        return
      }
      this.axios.post('/api/total', {
        Invoice_ID: this.invoice ? this.invoice.Invoice_ID : null,
        Invoice_Date: this.invoiceDate,
        Period_From: this.invoiceInterval[0],
        Period_To: this.invoiceInterval[1],
        Project_ID: this.project,
        Invoice_Total: this.totalPayment,
        Invoice_Creator: creatorId,
        Notes: this.notes,
        Division_Name: this.division,
        Invoice_Number: this.invoiceNumber,
        Payment_Period: this.paymentPeriod,
        Employee_Invoices: this.employeeInvoices,
        Discount: this.invoiceDiscount || 0
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.saveInProgress = false
        this.$emit(this.invoice ? 'updated' : 'added')
      }).catch(error => {
        this.saveInProgress = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    differenceBetween2Dates (date1, date2) {
      if (date1 && date2) {
        if (new Date(date2) < new Date(date1)) [date1, date2] = [date2, date1]
        const diffInTime = new Date(date2).getTime() - new Date(date1).getTime()
        const result = diffInTime / (1000 * 3600 * 24)
        this.paymentPeriod = result
        return result
      }
      return null
    },
    addDaysToDate (date, days) {
      const result = new Date(date)
      const realDays = +result.getDate() + +(days >= 0 ? days : 0)
      result.setDate(realDays)
      return formatLocalDate(result)
    },
    createInvoicePayment () {
      if (!this.currentInvoicePayment.Invoice_ID) {
        this.currentInvoicePayment.Invoice_ID = this.invoice.Invoice_ID
        this.currentInvoicePayment.Client_Name = this.invoice.Client_Name
      }
      this.clearInvoicePayment()
      this.invoicePaymentDialog = true
    },
    getInvoicePayments (invoiceId) {
      this.axios.get(`/api/invoice/payments/${invoiceId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then((res) => {
        this.$emit('onInvoicePaymentChange', res.data)
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addInvoicePayment () {
      this.addInvoicePaymentSaveDisable = true
      const body = {
        Invoice_ID: this.currentInvoicePayment.Invoice_ID,
        Invoice_Payment_Date: this.currentInvoicePayment.Payment_Date,
        Invoice_Payment_Total: this.currentInvoicePayment.Payment_Total
      }
      if (!this.currentInvoicePayment.Payment_ID) {
        this.axios.post('/api/invoice/payment', body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.getInvoicePayments(body.Invoice_ID)
          this.invoicePaymentDialog = false
          this.addInvoicePaymentSaveDisable = false
          this.$store.dispatch('setSuccess', { successMsg: 'Payment to invoice added' })
          this.$emit('paymentsUpdated')
        }).catch(error => {
          this.addInvoicePaymentSaveDisable = false
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        this.axios.put(`/api/invoice/payment/${this.currentInvoicePayment.Payment_ID}`, body, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.getInvoicePayments(body.Invoice_ID)
          this.invoicePaymentDialog = false
          this.addInvoicePaymentSaveDisable = false
          this.$store.dispatch('setSuccess', { successMsg: 'Payment updated' })
          this.$emit('paymentsUpdated')
        }).catch(error => {
          this.addInvoicePaymentSaveDisable = false
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      }
    },
    deleteInvoicePayment () {
      this.axios.delete(`/api/invoice/payment/${this.currentInvoicePayment.Payment_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getInvoicePayments(this.currentInvoicePayment.Invoice_ID)
        this.invoicePaymentDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Payment deleted' })
        this.$emit('paymentsUpdated')
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    cancelEditingInvoicePayment () {
      this.invoicePaymentDialog = false
      this.currentInvoicePayment.Payment_Date = formatLocalDate()
      this.currentInvoicePayment.Payment_Total = 0
    },
    onRowClick (e) {
      this.currentInvoicePayment = { ...e }
      this.currentInvoicePayment.Payment_Date = formatLocalDate(e.Payment_Date)
      this.invoicePaymentDialog = true
    },
    clearInvoicePayment () {
      this.currentInvoicePayment.Payment_ID = null
      this.currentInvoicePayment.Payment_Date = formatLocalDate()
      this.currentInvoicePayment.Payment_Total = this.totalPayment
    }
  },
  watch: {
    invoiceInterval (newVal, oldVal) {
      if ((newVal !== oldVal) && this.project) {
        this.getInvoiceTotal()
      }
    },
    project (newVal, oldVal) {
      if ((newVal !== oldVal) && this.invoiceInterval) {
        this.getInvoiceTotal()
      }
    },
    invoice (newVal, oldVal) {
      if (newVal) {
        this.creator = this.employee.find(item => item.employee_id === newVal.Invoice_Creator).full_name
        this.invoiceDueDate = newVal.Due_Date
        this.invoiceDate = newVal.Invoice_Date
        this.division = newVal.Division_Name
        this.project = newVal.Project_ID
        this.invoiceInterval = []
        this.invoiceInterval.push(newVal.Period_From)
        this.invoiceInterval.push(newVal.Period_To)
        this.invoiceNumber = newVal.Invoice_Number
        this.invoiceTotal = newVal.Invoice_Total
        this.paymentPeriod = newVal.Payment_Period
        this.notes = newVal.Notes
        this.invoiceDiscount = newVal.Discount || 0

        this.$nextTick(() => {
          if (this.project && this.invoiceInterval.length === 2) {
            this.getInvoiceTotal()
          }
        })
      } else {
        this.clearAll()
      }
    },
    employeeInvoices: {
      handler (newVal) {
        if (newVal && newVal.length > 0) {
          this.recalculateTotalWithDiscount()
        }
      },
      deep: true
    }
  },
  filters: {
    dateFilter (val) {
      return val ? formatLocalDate(val) : ''
    }
  }
}
</script>
<style lang="scss">
  .table-border {
    border-right: 1px solid #e0e0e0;
  }
  .table-border:last-child {
    border-right: none;
  }
  .invoice-notes {
    padding-bottom: 0px;
  }
  .table-container {
    height: 45vh;
    overflow-y: auto;
  }
  .total-container {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .disabled {
    pointer-events: none !important;
    opacity: 0.6;
  }
  .custom-input {
    margin-top: 0px !important;
    font-size: 14px !important;
    input {
      padding-top: 0px !important;
    }
  }
</style>
